import React, { useEffect, useState } from 'react';

import ClockIcon from 'icons/clock.svg';
import CourseCongratsSvg from 'icons/course-congrats.svg';
import CourseFallBackSvg from 'icons/course-fall-back.svg';
import IHascoBrandIcon from 'icons/ihasco-brand.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import useToasts from 'components/hooks/useToasts';

import ToastRack from 'components/application/ToastRack';

import ELearningLauncher from 'components/eLearning/ELearningLauncher';

import { sendAnalytics } from 'components/helpers/analytics';
import { formatDuration } from 'components/helpers/dates';

const defaultSeedRegistrationAttributes = {
  uuid: null,
  completionStatus: null,
  successStatus: null,
  score: null,
  completedAndPassed: false,
  completedAndPassedWithTest: false,
};

export default function ELearningPage({
  booking,
  bookingHashid,
  courseProvider,
  eLearningCourse,
  personnelFirstName,
  seedRegistration,
}) {
  const {
    coverImageUrl: eLearningCourseCoverImageUrl,
    custom: eLearningCourseCustom,
    description: eLearningCourseDescription,
    durationParts: eLearningCourseDurationParts,
    name: eLearningCourseName,
  } = eLearningCourse.data.attributes;

  const {
    completedAndPassed: seedCompletedAndPassed,
    completedAndPassedWithTest: seedCompletedAndPassedWithTest,
    completionStatus: seedRegistrationCompletionStatus,
    score: seedRegistrationScore,
    successStatus: seedRegistrationSuccessStatus,
    uuid: seedRegistrationUuid,
  } = seedRegistration?.data.attributes || defaultSeedRegistrationAttributes;

  const [registration, setRegistration] = useState({
    uuid: seedRegistrationUuid,
    completionStatus: seedRegistrationCompletionStatus,
    successStatus: seedRegistrationSuccessStatus,
    score: seedRegistrationScore,
    completedAndPassed: seedCompletedAndPassed,
    completedAndPassedWithTest: seedCompletedAndPassedWithTest,
  });

  const [toasts, , , , addInedibleToast] = useToasts();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'eLearning.ELearningPage',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  useEffect(() => {
    const { course, personnel } = booking.data.relationships;

    sendAnalytics('eLearning page viewed', {
      courseId: course.data.id,
      personnelId: personnel.data.id,
      registrationUuid: registration.uuid,
    });
  }, []);

  return (
    <>
      <div className='tw-flex tw-justify-center tw-bg-grey-025'>
        <div className='e-learning__main-content tw-border-grey-100 tw-bg-white'>
          {registration.completedAndPassed ?
            <>
              <div className='e-learning__column e-learning__column--left'>
                <h2 className='tw-m-0 tw-mb-6 tw-text-2xl tw-font-semibold tw-tracking-tighter tw-text-grey-900'>
                  {i18nT('completedAndPassed.title', {
                    firstName: personnelFirstName,
                    interpolation: { escapeValue: false },
                  })}
                </h2>
                <div className='tw-mb-6'>
                  {registration.completedAndPassedWithTest ?
                    <Trans
                      components={{
                        span: <span className='tw-font-medium' />,
                      }}
                      i18nKey='eLearning.ELearningPage.completedAndPassed.descriptions.passedWithTest'
                      ns='components'
                      values={{ score: registration.score }}
                    />
                  : i18nT('completedAndPassed.descriptions.completed')}
                </div>
                <div className='tw-mb-6'>
                  <Trans
                    i18nKey='eLearning.ELearningPage.completedAndPassed.descriptions.trainingRecords'
                    ns='components'
                  />
                </div>
                <div>
                  {i18nT('completedAndPassed.descriptions.closeWindow')}
                </div>
              </div>
              <div className='e-learning__column e-learning__column--right tw-flex tw-justify-center'>
                <CourseCongratsSvg className='tw-h-auto tw-w-full' />
              </div>
            </>
          : <>
              <div className='e-learning__column e-learning__column--left'>
                <div className='tw-mb-6'>
                  <h2 className='tw-m-0 tw-text-2xl tw-font-semibold tw-tracking-tighter tw-text-grey-900'>
                    {eLearningCourseName}
                  </h2>
                  {eLearningCourseDurationParts && (
                    <div className='tw-mt-1 tw-flex'>
                      <ClockIcon
                        className='tw-mr-1 [&_path]:tw-fill-grey-500'
                        height='20px'
                        width='20px'
                      />
                      <span>
                        {i18nT('course.duration', {
                          duration: formatDuration({
                            durationParts: eLearningCourseDurationParts,
                          }),
                        })}
                      </span>
                    </div>
                  )}
                </div>
                <div className='e-learning__state tw-mb-6 tw-border-0 tw-bg-cyan-025 tw-text-cyan-800'>
                  <div className='tw-text-xs tw-font-semibold tw-tracking-wide'>
                    {registration.uuid ?
                      i18nTShared('inProgress')
                    : i18nTShared('notStarted')}
                  </div>
                </div>
                {eLearningCourseDescription && (
                  <div className='ws-pre-wrap tw-mb-6'>
                    {eLearningCourseDescription}
                  </div>
                )}
                {(!eLearningCourseCustom || courseProvider) && (
                  <div className='tw-relative tw-mb-6 tw-flex tw-items-center'>
                    <span>{i18nT('course.providedBy')}</span>
                    {!eLearningCourseCustom ?
                      <IHascoBrandIcon
                        className='tw-relative tw-bottom-0.5 tw-ml-1'
                        height='24px'
                        width='82px'
                      />
                    : <span className='tw-font-medium'>{courseProvider}</span>}
                  </div>
                )}
                <div className='tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
                  <div className='tw-mb-5'>
                    <Trans
                      components={{
                        span: <span className='tw-font-semibold' />,
                      }}
                      i18nKey='eLearning.ELearningPage.saveProgress'
                      ns='components'
                    />
                  </div>
                  <div className='tw-mb-5'>
                    {i18nT(
                      `stableInternetConnection.${registration.uuid ? 'withUuid' : 'default'}`,
                    )}
                  </div>
                  <ELearningLauncher
                    booking={booking}
                    bookingHashid={bookingHashid}
                    onError={addInedibleToast}
                    onRegistrationUpdate={(updateProps) =>
                      setRegistration({ ...registration, ...updateProps })
                    }
                    registrationUuid={registration.uuid}
                  />
                </div>
                <div className='tw-mt-6'>
                  <span className='tw-font-semibold'>
                    {i18nT('support.title')}
                  </span>
                  <Trans
                    components={{
                      a: (
                        /* eslint-disable jsx-a11y/anchor-has-content */
                        <a
                          className='app-link va-baseline tw-font-semibold tw-text-blue-500 hover:tw-text-blue-300'
                          href='https://intercom.help/hands-hq/en/articles/6563616-elearning-course-not-launching-troubleshooting-guide'
                        />
                        /* eslint-enable jsx-a11y/anchor-has-content */
                      ),
                      span: <span className='tw-text-grey-600' />,
                    }}
                    i18nKey='eLearning.ELearningPage.support.description'
                    ns='components'
                  />
                </div>
              </div>
              <div className='e-learning__column e-learning__column--right'>
                {eLearningCourseCoverImageUrl ?
                  <img
                    alt='Course cover'
                    className='tw-h-auto tw-w-full'
                    src={eLearningCourseCoverImageUrl}
                  />
                : <CourseFallBackSvg className='tw-h-auto tw-w-full' />}
              </div>
            </>
          }
        </div>
      </div>
      <ToastRack toasts={toasts} />
    </>
  );
}

ELearningPage.propTypes = {
  personnelFirstName: PropTypes.string.isRequired,
  bookingHashid: PropTypes.string.isRequired,
  booking: PropTypes.shape({
    data: PropTypes.shape({
      relationships: PropTypes.shape({
        personnel: PropTypes.object.isRequired,
        course: PropTypes.object.isRequired,
      }),
    }),
  }),
  courseProvider: PropTypes.string,
  eLearningCourse: PropTypes.shape({
    data: PropTypes.shape({
      attributes: PropTypes.shape({
        coverImageUrl: PropTypes.string,
        custom: PropTypes.bool.isRequired,
        description: PropTypes.string,
        durationParts: PropTypes.shape({
          days: PropTypes.number,
          hours: PropTypes.number,
          minutes: PropTypes.number,
          months: PropTypes.number,
          seconds: PropTypes.number,
          weeks: PropTypes.number,
          years: PropTypes.number,
        }),
        name: PropTypes.string.isRequired,
      }),
    }),
  }),
  seedRegistration: PropTypes.shape({
    data: PropTypes.shape({
      attributes: PropTypes.shape({
        uuid: PropTypes.string,
        completionStatus: PropTypes.string,
        successStatus: PropTypes.string,
        score: PropTypes.number,
        completedAndPassed: PropTypes.bool,
        completedAndPassedWithTest: PropTypes.bool,
      }),
    }),
  }),
};
